import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, Truck, Download, Copy, Check } from 'lucide-react';
import orderService from '../services/orderService';
import { showSuccess, showError } from '../services/toastService';
import './OrderManagement.css';

// UI Components
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import InfiniteScrollSentinel from './InfiniteScrollSentinel';

const OrderManagement = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [copiedTrackingUrl, setCopiedTrackingUrl] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        processing: 0,
        shipped: 0,
        delivered: 0,
        cancelled: 0
    });
    const [filters, setFilters] = useState(() => ({
        search: searchParams.get('search') || '',
        status: searchParams.get('status') || '',
        startDate: searchParams.get('start') || '',
        endDate: searchParams.get('end') || ''
    }));
    const [statusUpdate, setStatusUpdate] = useState({
        status: '',
        courier_name: '',
        tracking_number: '',
        tracking_url: '',
        notes: ''
    });

    useEffect(() => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            const values = { search: filters.search, status: filters.status, start: filters.startDate, end: filters.endDate };
            Object.entries(values).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
            next.delete('page');
            return next;
        }, { replace: true });
    }, [filters, setSearchParams]);

    useEffect(() => {
        fetchOrders();
        fetchStats();
    }, [pagination.page, filters]);

    const fetchStats = async () => {
        try {
            const response = await orderService.getAllOrders({ limit: 1000 });
            if (response.success) {
                const allOrders = response.data.orders;
                setStats({
                    total: allOrders.length,
                    pending: allOrders.filter(o => o.status === 'pending').length,
                    processing: allOrders.filter(o => o.status === 'processing').length,
                    shipped: allOrders.filter(o => o.status === 'shipped').length,
                    delivered: allOrders.filter(o => o.status === 'delivered').length,
                    cancelled: allOrders.filter(o => o.status === 'cancelled').length
                });
            }
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...filters
            };
            const response = await orderService.getAllOrders(params);
            if (response.success) {
                setOrders((current) => pagination.page === 1 ? response.data.orders : [...current, ...response.data.orders]);
                setPagination(response.data.pagination);
            }
        } catch (error) {
            showError(error.message || 'Failed to fetch orders');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleClearFilters = () => {
        setFilters({
            search: '',
            status: '',
            startDate: '',
            endDate: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const hasActiveFilters = () => {
        return filters.search !== '' || (filters.status !== '' && filters.status !== 'all') || filters.startDate !== '' || filters.endDate !== '';
    };

    const handleViewDetails = (order) => {
        setSelectedOrder(order);
        setDetailsModalOpen(true);
    };

    const handleUpdateStatus = (order) => {
        console.log('Opening status modal for order:', order); // Debug log
        setSelectedOrder(order);
        const initialStatus = {
            status: order.status || '',
            courier_name: order.courier_name || '',
            tracking_number: order.tracking_number || '',
            tracking_url: order.tracking_url || '',
            notes: order.notes || ''
        };
        console.log('Initial status update state:', initialStatus); // Debug log
        setStatusUpdate(initialStatus);
        setStatusModalOpen(true);
    };

    const handleStatusSubmit = async () => {
        console.log('Submitting status update:', statusUpdate); // Debug log

        if (!statusUpdate.status) {
            showError('Please select a status');
            return;
        }

        // Only validate shipping fields when status is 'shipped'
        if (statusUpdate.status === 'shipped') {
            if (!statusUpdate.courier_name || !statusUpdate.tracking_number || !statusUpdate.tracking_url) {
                showError('Courier name, tracking number, and tracking URL are required for shipped status');
                return;
            }
        }

        // Prepare data - only send shipping fields if status is 'shipped'
        const submitData = {
            status: statusUpdate.status,
            notes: statusUpdate.notes || ''
        };

        // Only include shipping fields for 'shipped' status
        if (statusUpdate.status === 'shipped') {
            submitData.courier_name = statusUpdate.courier_name;
            submitData.tracking_number = statusUpdate.tracking_number;
            submitData.tracking_url = statusUpdate.tracking_url;
        }

        setLoading(true);
        try {
            console.log('Calling API with:', { orderId: selectedOrder.id, data: submitData }); // Debug log
            const response = await orderService.updateOrderStatus(selectedOrder.id, submitData);
            console.log('API Response:', response); // Debug log

            if (response.success) {
                showSuccess('Order status updated successfully');
                fetchOrders();
                setStatusModalOpen(false);
                // Reset the status update state
                setStatusUpdate({
                    status: '',
                    courier_name: '',
                    tracking_number: '',
                    tracking_url: '',
                    notes: ''
                });
            } else {
                showError(response.message || 'Failed to update order status');
            }
        } catch (error) {
            console.error('Status update error:', error); // Debug log

            // Handle validation errors from backend
            if (error.errors && Array.isArray(error.errors)) {
                const errorMessages = error.errors.map(err => `${err.field}: ${err.message}`).join('\n');
                showError(`Validation failed:\n${errorMessages}`);
            } else {
                showError(error.message || 'Failed to update order status');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadInvoice = (order) => {
        // Generate invoice PDF (implement with jsPDF or similar)
        showSuccess('Invoice download started');
    };

    const handleCopyTrackingUrl = async (trackingUrl) => {
        console.log('Copy function called with URL:', trackingUrl);
        try {
            await navigator.clipboard.writeText(trackingUrl);
            setCopiedTrackingUrl(true);
            showSuccess('Tracking URL copied to clipboard');
            console.log('Copy successful');

            // Reset the copied state after 2 seconds
            setTimeout(() => {
                setCopiedTrackingUrl(false);
            }, 2000);
        } catch (error) {
            console.error('Failed to copy tracking URL:', error);
            showError('Failed to copy tracking URL');
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'pending',
            processing: 'pending',
            shipped: 'pending',
            delivered: 'active',
            cancelled: 'destructive'
        };
        return colors[status] || 'inactive';
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    const formatPhone = (phone, countryCode = '+91') => {
        if (!phone) return 'N/A';
        // If phone already has country code, return as is
        if (phone.startsWith('+')) return phone;
        // Otherwise, prepend country code
        return `${countryCode} ${phone}`;
    };

    return (
        <div className="order-management-container">
            {/* Header */}
            <div className="om-header">
                <div>
                    <h1 className="om-title">Orders</h1>
                    <p className="om-subtitle">Manage all customer orders</p>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="om-stats-grid">
                <div className="om-stat-card om-stat-total">
                    <div className="om-stat-value">{stats.total}</div>
                    <div className="om-stat-label">Total Orders</div>
                </div>

                <div className="om-stat-card om-stat-pending">
                    <div className="om-stat-value">{stats.pending}</div>
                    <div className="om-stat-label">Pending</div>
                </div>

                <div className="om-stat-card om-stat-processing">
                    <div className="om-stat-value">{stats.processing}</div>
                    <div className="om-stat-label">Processing</div>
                </div>

                <div className="om-stat-card om-stat-shipped">
                    <div className="om-stat-value">{stats.shipped}</div>
                    <div className="om-stat-label">Shipped</div>
                </div>

                <div className="om-stat-card om-stat-delivered">
                    <div className="om-stat-value">{stats.delivered}</div>
                    <div className="om-stat-label">Delivered</div>
                </div>

                <div className="om-stat-card om-stat-cancelled">
                    <div className="om-stat-value">{stats.cancelled}</div>
                    <div className="om-stat-label">Cancelled</div>
                </div>
            </div>

            {/* Orders Table */}
            <Card>
                <CardHeader>
                    <CardTitle>Orders List</CardTitle>
                </CardHeader>
                <CardContent>
                    {/* Filters */}
                    <div className="om-filters">
                        <Input
                            type="text"
                            name="search"
                            value={filters.search}
                            onChange={handleFilterChange}
                            placeholder="Search by order number or buyer name..."
                            className="om-search-input"
                        />
                        <Select
                            value={filters.status || 'all'}
                            onValueChange={(value) => {
                                setFilters(prev => ({ ...prev, status: value === 'all' ? '' : value }));
                                setPagination(prev => ({ ...prev, page: 1 }));
                            }}
                        >
                            <SelectTrigger className="om-select-filter">
                                <SelectValue placeholder="All Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="processing">Processing</SelectItem>
                                <SelectItem value="shipped">Shipped</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                        </Select>
                        <Input
                            type="date"
                            name="startDate"
                            value={filters.startDate}
                            onChange={handleFilterChange}
                            className="om-date-input"
                        />
                        <Input
                            type="date"
                            name="endDate"
                            value={filters.endDate}
                            onChange={handleFilterChange}
                            className="om-date-input"
                        />
                        <Button
                            onClick={handleClearFilters}
                            disabled={!hasActiveFilters()}
                            className="om-clear-filters-btn"
                            style={{
                                backgroundColor: hasActiveFilters() ? '#111827' : '#d1d5db',
                                color: '#ffffff',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                cursor: hasActiveFilters() ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '14px',
                                fontWeight: '500',
                                opacity: hasActiveFilters() ? 1 : 0.6,
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                            }}
                        >
                            Clear Filters
                        </Button>
                    </div>

                    {/* Orders Table */}
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Order #</TableHead>
                                <TableHead>Buyer</TableHead>
                                <TableHead>Items</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Tracking</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && orders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan="8" className="om-table-empty">Loading...</TableCell>
                                </TableRow>
                            ) : orders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan="8" className="om-table-empty">No orders found</TableCell>
                                </TableRow>
                            ) : (
                                orders.map(order => (
                                    <TableRow key={order.id}>
                                        <TableCell className="om-order-number">{order.order_number}</TableCell>
                                        <TableCell>
                                            <div>
                                                <div style={{ fontWeight: 600 }}>{order.buyer.business_name}</div>
                                                <div style={{ fontSize: '13px', color: '#111827', fontWeight: 500, marginTop: '2px' }}>
                                                    {formatPhone(order.buyer.phone, order.buyer.country_code)}
                                                </div>
                                                {order.buyer.email && (
                                                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                                        {order.buyer.email}
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>{order.items.length} item(s)</TableCell>
                                        <TableCell style={{ fontWeight: 600 }}>{formatCurrency(order.total_amount)}</TableCell>
                                        <TableCell>
                                            <Badge variant={getStatusColor(order.status)}>
                                                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {order.tracking_url ? (
                                                <div className="om-tracking-url-container">
                                                    <a
                                                        href={order.tracking_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: '#3b82f6',
                                                            textDecoration: 'none',
                                                            fontSize: '12px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                        title={`Track with ${order.courier_name || 'courier'}`}
                                                    >
                                                        Track
                                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                                            <polyline points="15,3 21,3 21,9"></polyline>
                                                            <line x1="10" y1="14" x2="21" y2="3"></line>
                                                        </svg>
                                                    </a>
                                                    <button
                                                        onClick={() => handleCopyTrackingUrl(order.tracking_url)}
                                                        className={`om-copy-button ${copiedTrackingUrl ? 'copied' : ''}`}
                                                        title="Copy tracking URL"
                                                        style={{ padding: '2px 6px', fontSize: '10px', minWidth: '45px' }}
                                                    >
                                                        {copiedTrackingUrl ? (
                                                            <Check size={10} />
                                                        ) : (
                                                            <Copy size={10} />
                                                        )}
                                                    </button>
                                                </div>
                                            ) : order.tracking_number ? (
                                                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                                    {order.tracking_number}
                                                </span>
                                            ) : (
                                                <span style={{ fontSize: '12px', color: '#9ca3af' }}>-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>{formatDate(order.created_at)}</TableCell>
                                        <TableCell>
                                            <div className="om-actions">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleViewDetails(order)}
                                                    className="om-view-btn"
                                                    title="View details"
                                                >
                                                    <Eye size={16} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleUpdateStatus(order)}
                                                    className="om-status-btn"
                                                    title="Update status"
                                                >
                                                    <Truck size={16} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDownloadInvoice(order)}
                                                    className="om-download-btn"
                                                    title="Download invoice"
                                                >
                                                    <Download size={16} />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <InfiniteScrollSentinel
                        hasMore={pagination.page < pagination.pages}
                        loading={loading}
                        onLoadMore={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    />
                </CardContent>
            </Card>

            {/* Order Details Modal */}
            <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
                <DialogContent className="om-details-dialog" style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
                    {selectedOrder && (
                        <div>
                            <DialogHeader>
                                <DialogTitle>Order Details - {selectedOrder.order_number}</DialogTitle>
                            </DialogHeader>

                            <div style={{ marginTop: '24px' }}>
                                {/* Buyer Info */}
                                <div style={{ marginBottom: '24px' }}>
                                    <h3 style={{ fontWeight: 600, marginBottom: '12px' }}>Buyer Information</h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <div>
                                            <label style={{ fontSize: '12px', color: '#6b7280' }}>Business Name</label>
                                            <p style={{ fontWeight: 500 }}>{selectedOrder.buyer.business_name}</p>
                                        </div>
                                        <div>
                                            <label style={{ fontSize: '12px', color: '#6b7280' }}>Phone Number</label>
                                            <p style={{ fontWeight: 600, fontSize: '15px' }}>
                                                {formatPhone(selectedOrder.buyer.phone, selectedOrder.buyer.country_code)}
                                            </p>
                                        </div>
                                        {selectedOrder.buyer.email && (
                                            <div>
                                                <label style={{ fontSize: '12px', color: '#6b7280' }}>Email</label>
                                                <p style={{ fontWeight: 500 }}>{selectedOrder.buyer.email}</p>
                                            </div>
                                        )}
                                        <div>
                                            <label style={{ fontSize: '12px', color: '#6b7280' }}>Order Date</label>
                                            <p style={{ fontWeight: 500 }}>{formatDate(selectedOrder.created_at)}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Order Items */}
                                <div style={{ marginBottom: '24px' }}>
                                    <h3 style={{ fontWeight: 600, marginBottom: '12px' }}>Order Items</h3>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Product</th>
                                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Qty</th>
                                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Price</th>
                                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedOrder.items.map((item, idx) => (
                                                    <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                        <td style={{ padding: '12px' }}>
                                                            <div>
                                                                <div style={{ fontWeight: 500 }}>{item.product_name}</div>
                                                                <div style={{ fontSize: '12px', color: '#6b7280' }}>{item.product_sku}</div>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '12px' }}>{item.quantity}</td>
                                                        <td style={{ padding: '12px' }}>{formatCurrency(item.unit_price)}</td>
                                                        <td style={{ padding: '12px', fontWeight: 600 }}>{formatCurrency(item.total_price)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Order Summary */}
                                <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span>Total Amount:</span>
                                        <span style={{ fontWeight: 600 }}>{formatCurrency(selectedOrder.total_amount)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span>Status:</span>
                                        <Badge variant={getStatusColor(selectedOrder.status)}>
                                            {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                                        </Badge>
                                    </div>
                                    {selectedOrder.courier_name && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                            <span>Courier:</span>
                                            <span style={{ fontWeight: 500 }}>{selectedOrder.courier_name}</span>
                                        </div>
                                    )}
                                    {selectedOrder.tracking_number && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                            <span>Tracking Number:</span>
                                            <span style={{ fontWeight: 500 }}>{selectedOrder.tracking_number}</span>
                                        </div>
                                    )}
                                    {selectedOrder.tracking_url && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                            <span>Track Shipment:</span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <a
                                                    href={selectedOrder.tracking_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: '#3b82f6',
                                                        textDecoration: 'none',
                                                        fontWeight: 500,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                >
                                                    Track Package
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                                        <polyline points="15,3 21,3 21,9"></polyline>
                                                        <line x1="10" y1="14" x2="21" y2="3"></line>
                                                    </svg>
                                                </a>
                                                <button
                                                    onClick={() => handleCopyTrackingUrl(selectedOrder.tracking_url)}
                                                    className={`om-copy-button ${copiedTrackingUrl ? 'copied' : ''}`}
                                                    title="Copy tracking URL"
                                                >
                                                    {copiedTrackingUrl ? (
                                                        <>
                                                            <Check size={12} />
                                                            Copied
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Copy size={12} />
                                                            Copy
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {selectedOrder.notes && (
                                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                                            <span style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Notes:</span>
                                            <span style={{ fontSize: '14px', fontStyle: 'italic' }}>{selectedOrder.notes}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Update Status Modal */}
            <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
                <DialogContent className="om-status-modal">
                    <DialogHeader className="om-modal-header">
                        <DialogTitle className="om-modal-title">
                            Update Order Status
                        </DialogTitle>
                        <DialogDescription className="om-modal-description">
                            Update the status and shipping details for order <strong>{selectedOrder?.order_number}</strong>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="om-modal-content">
                        {/* Status Selection Section */}
                        <div className="om-form-section">
                            <div className="om-form-group">
                                <Label className="om-form-label">
                                    Order Status <span className="om-required">*</span>
                                </Label>
                                <select
                                    value={statusUpdate.status || ''}
                                    onChange={(e) => {
                                        console.log('Status changed to:', e.target.value); // Debug log
                                        setStatusUpdate(prev => ({ ...prev, status: e.target.value }));
                                    }}
                                    className="om-status-select"
                                >
                                    <option value="">Select status</option>
                                    <option value="pending">Pending</option>
                                    <option value="processing">Processing</option>
                                    <option value="shipped">Shipped</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>
                        </div>

                        {/* Shipping Details Section */}
                        {statusUpdate.status === 'shipped' && (
                            <div className="om-form-section om-shipping-section">
                                <h4 className="om-section-title">Shipping Details</h4>

                                <div className="om-form-row">
                                    <div className="om-form-group">
                                        <Label className="om-form-label">
                                            Courier Name <span className="om-required">*</span>
                                        </Label>
                                        <Input
                                            value={statusUpdate.courier_name}
                                            onChange={(e) => setStatusUpdate(prev => ({ ...prev, courier_name: e.target.value }))}
                                            placeholder="e.g., Delhivery, Blue Dart, FedEx"
                                            className="om-form-input"
                                        />
                                    </div>

                                    <div className="om-form-group">
                                        <Label className="om-form-label">
                                            Tracking Number <span className="om-required">*</span>
                                        </Label>
                                        <Input
                                            value={statusUpdate.tracking_number}
                                            onChange={(e) => setStatusUpdate(prev => ({ ...prev, tracking_number: e.target.value }))}
                                            placeholder="Enter tracking number"
                                            className="om-form-input"
                                        />
                                    </div>
                                </div>

                                <div className="om-form-group">
                                    <Label className="om-form-label">
                                        Tracking URL <span className="om-required">*</span>
                                    </Label>
                                    <div className="om-tracking-url-input-container">
                                        <Input
                                            value={statusUpdate.tracking_url}
                                            onChange={(e) => setStatusUpdate(prev => ({ ...prev, tracking_url: e.target.value }))}
                                            placeholder="https://www.delhivery.com/track/package/..."
                                            className="om-form-input"
                                        />
                                        {statusUpdate.tracking_url && (
                                            <button
                                                type="button"
                                                onClick={() => handleCopyTrackingUrl(statusUpdate.tracking_url)}
                                                className={`om-copy-button ${copiedTrackingUrl ? 'copied' : ''}`}
                                                title="Copy tracking URL"
                                            >
                                                {copiedTrackingUrl ? (
                                                    <>
                                                        <Check size={14} />
                                                        Copied
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy size={14} />
                                                        Copy
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                    <small className="om-form-hint">
                                        Examples: Delhivery, Blue Dart, DTDC, FedEx, DHL tracking URLs
                                    </small>
                                </div>
                            </div>
                        )}

                        {/* Notes Section */}
                        <div className="om-form-section">
                            <div className="om-form-group">
                                <Label className="om-form-label">Notes (Optional)</Label>
                                <textarea
                                    value={statusUpdate.notes}
                                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Add any notes about this order..."
                                    rows="3"
                                    className="om-form-textarea"
                                />
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="om-modal-footer">
                        <Button variant="outline" onClick={() => setStatusModalOpen(false)} className="om-cancel-btn">
                            Cancel
                        </Button>
                        <Button onClick={handleStatusSubmit} disabled={loading} className="om-submit-btn">
                            {loading ? 'Updating...' : 'Update Status'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div >
    );
};

export default OrderManagement;
